import { Component, inject, OnInit, ViewChild } from "@angular/core";
import { BpModalCanDismiss } from "@models/interfaces/bp-modal-can-dismiss";
import { Observable, take, tap } from "rxjs";
import { Account, LegalRightsRevokedError } from "@models";
import { AccountService } from "../../../account/services/account.service";
import { AuthService } from "bp-authorization-lib";
import { CustomModal } from "../../../core/components/base-modals";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
	DYNAMIC_TERMS_TOKEN,
	TermsAndConditionsUpdatesComponent
} from "@modules/shared/terms-and-conditions/components/terms-and-conditions-updates.component";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import { AgreementBundle, UserJourney } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { Router } from "@angular/router";

@UntilDestroy()
@Component({
	selector: "app-legal-acceptance",
	templateUrl: "./legal-acceptance.component.html",
	styleUrls: ["./legal-acceptance.component.scss"],
	standalone: false
})
export class LegalAcceptanceComponent extends CustomModal implements BpModalCanDismiss, OnInit {
	protected termsAndConditionsService: TermsAndConditionsService = inject(TermsAndConditionsService);

	@ViewChild(DYNAMIC_TERMS_TOKEN) dynamic_terms: TermsAndConditionsUpdatesComponent;
	public data: {
		error: LegalRightsRevokedError;
		bundles: AgreementBundle[];
		redirectTo: string;
	};

	private acceptedButtonClick: boolean = false;
	public bundles: AgreementBundle[] = [];
	protected dynamictermAndConditionActive = false;
	public isDynamictermAndConditionActive$ = this.termsAndConditionsService.isDynamictermAndConditionActive$.pipe(
		tap((isActive) => {
			this.dynamictermAndConditionActive = isActive;
		})
	);
	private userJourneys: UserJourney[];

	public constructor(
		private accountService: AccountService,
		private authorization: AuthService,
		private readonly router: Router
	) {
		super();
		this.accountService.accountUpdated.pipe(untilDestroyed(this)).subscribe(() => {
			if (this.modalCanDismiss()) {
				this.close();
			}
		});
	}

	private getUserJourneysFromError(): UserJourney[] {
		if (!this.data?.error.responseErrors) {
			return [];
		}
		return [
			...new Set(
				this.data.error.responseErrors
					.filter((error) => +error.status === 406 && error.code === "terms.mandatory.not.accepted")
					.flatMap((error) => error.meta)
			)
		];
	}

	public ngOnInit(): void {
		this.userJourneys = this.getUserJourneys();
		if (this.userJourneys) {
			this.termsAndConditionsService
				.getAgreementBundleData$(this.userJourneys)
				.pipe(take(1))
				.subscribe((bundles) => {
					this.bundles = bundles;
				});
		}
	}

	private getUserJourneys(): UserJourney[] {
		if (this.data.error) {
			return this.getUserJourneysFromError();
		}
		if (this.data.bundles?.length > 0) {
			return [UserJourney.ChangesNotification];
		}
		return [];
	}

	public get account(): Account {
		return this.accountService.account;
	}

	public set account(a: Account) {
		this.accountService.account = a;
	}

	public get acceptedAllTerms(): boolean {
		return this.accountService.acceptedTerms();
	}

	public save(): void {
		// Do nothing on pressing enter
	}

	public modalCanDismiss(): boolean | Observable<boolean> {
		if (this.dynamictermAndConditionActive) {
			return this.acceptedButtonClick;
		}
		return this.accountService.acceptedTerms() && this.acceptedButtonClick;
	}

	public onClickAccept(): void {
		if (this.dynamictermAndConditionActive) {
			if (this.dynamicTermsInvalid) {
				this.dynamic_terms?.wiggleIt();
				return;
			}
			this.continue();
		} else {
			this.continue();
		}
	}

	public continue(): void {
		if (this.isButtonDisabled) {
			return;
		}
		this.acceptedButtonClick = true;

		if (this.dynamictermAndConditionActive) {
			this.termsAndConditionsService.acceptTerms(
				this.userJourneys,
				this.dynamic_terms?.termsAndConditionsForm?.value,
				() => {
					this.close();
					this.modalService.unhideAll();
					const redirectTo = this.data.redirectTo;
					if (redirectTo) {
						this.router.navigateByUrl(redirectTo);
					}
				}
			);
		} else {
			this.accountService.update();
		}
	}

	public onClickDecline(): void {
		if (this.dynamictermAndConditionActive) {
			this.router.navigate(["/user/logout"]);
		} else {
			this.authorization.logoutOAuth();
		}
	}

	public get dynamicTermsInvalid(): boolean {
		return this.dynamictermAndConditionActive && !!this.dynamic_terms?.termsAndConditionsForm?.invalid;
	}

	public get isButtonDisabled(): boolean {
		const staticTermsInvalid = !this.dynamictermAndConditionActive && !this.acceptedAllTerms;
		return this.dynamicTermsInvalid || staticTermsInvalid;
	}
}
